import { TokenQuantity } from './chunk-O2NVDZWR.js';
import { SubCard } from './chunk-O65HCPWR.js';
import { ProtocolIcon } from './chunk-EPXDPMBL.js';
import { ProtocolBadge } from './chunk-XCW4JPLG.js';
import { LIFI, LIFI_BRIDGE_CALLS, LIFI_COMPONENTS, pickField, decodedOrRaw, pickFrom, LIFI_FIELD_LABELS, ProtocolValue, SectionCard, FactGrid, ProtocolParamList } from './chunk-WCNTYBII.js';
import { Route } from 'lucide-react';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';

function MinAmount({ value }) {
  return /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1", children: [
    /* @__PURE__ */ jsx(ProtocolValue, { value }),
    /* @__PURE__ */ jsx(
      "span",
      {
        className: "text-2xs uppercase tracking-wide text-muted-foreground",
        title: "Minimum specified in this call. Receipts establish the amount actually bridged and delivered.",
        children: "(min)"
      }
    )
  ] });
}
function swapFacts(swap) {
  const get = (...names) => pickFrom(swap, ...names);
  const fromToken = get("sendingAssetId", "fromAssetId", "fromToken");
  const fromAmount = get("fromAmount", "amountIn", "inputAmount");
  const toToken = get("receivingAssetId", "toAssetId", "toToken");
  const router = get("callTo", "approveTo");
  return [
    { label: LIFI_FIELD_LABELS.fromToken, value: /* @__PURE__ */ jsx(ProtocolValue, { value: fromToken }), when: fromToken },
    { label: LIFI_FIELD_LABELS.fromAmount, value: /* @__PURE__ */ jsx(ProtocolValue, { value: fromAmount }), when: fromAmount != null },
    { label: LIFI_FIELD_LABELS.toToken, value: /* @__PURE__ */ jsx(ProtocolValue, { value: toToken }), when: toToken },
    { label: LIFI_FIELD_LABELS.swapRouter, value: /* @__PURE__ */ jsx(ProtocolValue, { value: router }), when: router }
  ];
}
function LiFiBridge({ call: call2, hint }) {
  const bridge = pickField(call2.params, "bridgeData");
  const swaps = decodedOrRaw(pickField(call2.params, "swapData"));
  const sourceSwaps = Array.isArray(swaps) ? swaps : [];
  const mayan = pickField(call2.params, "mayanData");
  const bget = (...names) => pickFrom(bridge, ...names);
  const bridgedToken = bget("sendingAssetId", "fromAssetId", "fromToken");
  const minAmount = bget("minAmount", "minAmountOut", "amount");
  const recipient = bget("receiver", "receivingAddress", "to", "recipient");
  const destChain = bget("destinationChainId", "destChainId", "toChainId");
  const bridgeName = bget("bridge");
  const transferId = bget("transactionId", "txId", "transferId");
  const destinationCall = decodedOrRaw(bget("hasDestinationCall"));
  const mayanProtocol = pickFrom(mayan, "mayanProtocol", "protocol");
  const hasBridge = bridgedToken != null || recipient != null || minAmount != null || destChain != null;
  const title = /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-2", children: [
    /* @__PURE__ */ jsx(ProtocolIcon, { protocolId: LIFI.protocolId, icon: Route }),
    /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold", children: hint.label ?? call2.label ?? "Bridge via LI.FI" })
  ] });
  const facts = [
    { label: LIFI_FIELD_LABELS.bridgedToken, value: /* @__PURE__ */ jsx(ProtocolValue, { value: bridgedToken }), when: bridgedToken },
    { label: LIFI_FIELD_LABELS.minAmount, value: /* @__PURE__ */ jsx(MinAmount, { value: minAmount }), when: minAmount != null },
    { label: LIFI_FIELD_LABELS.recipient, value: /* @__PURE__ */ jsx(ProtocolValue, { value: recipient }), when: recipient },
    { label: LIFI_FIELD_LABELS.destChain, value: /* @__PURE__ */ jsx(ProtocolValue, { value: destChain }), when: destChain != null },
    { label: LIFI_FIELD_LABELS.bridge, value: /* @__PURE__ */ jsx(ProtocolValue, { value: bridgeName }), when: bridgeName },
    { label: LIFI_FIELD_LABELS.mayanProtocol, value: /* @__PURE__ */ jsx(ProtocolValue, { value: mayanProtocol }), when: mayanProtocol },
    { label: "Destination action", value: destinationCall === true || destinationCall === "true" ? "Requested after delivery; check the destination execution" : "No destination action requested", when: destinationCall === true || destinationCall === false || destinationCall === "true" || destinationCall === "false" },
    { label: LIFI_FIELD_LABELS.transactionId, value: /* @__PURE__ */ jsx(ProtocolValue, { value: transferId }), when: transferId }
  ];
  return /* @__PURE__ */ jsxs(SectionCard, { title, protocolId: LIFI.protocolId, meta: /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: LIFI.protocolId }), children: [
    hasBridge ? /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(FactGrid, { facts }),
      sourceSwaps.map((swap, index) => /* @__PURE__ */ jsx("div", { className: "mt-2", children: /* @__PURE__ */ jsx(SubCard, { caption: sourceSwaps.length === 1 ? "Source swap" : `Source swap ${index + 1} of ${sourceSwaps.length}`, children: /* @__PURE__ */ jsx(FactGrid, { facts: swapFacts(swap) }) }) }, index))
    ] }) : (
      // The facet calldata didn't decode into the expected BridgeData — show the
      // generic argument list rather than an empty card.
      /* @__PURE__ */ jsx(ProtocolParamList, { params: call2.params, emptyLabel: "No decoded arguments" })
    ),
    /* @__PURE__ */ jsxs("details", { className: "mt-3 min-w-0", children: [
      /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground hover:text-foreground", children: "All call arguments" }),
      /* @__PURE__ */ jsx("div", { className: "pt-2", children: /* @__PURE__ */ jsx(ProtocolParamList, { params: call2.params, emptyLabel: "No decoded arguments" }) })
    ] })
  ] });
}
function LiFiOutcome({ event, chain, renderAddress }) {
  const recovered = event.name === "LiFiTransferRecovered";
  const address = (value) => typeof value === "string" && renderAddress ? renderAddress(value, { chain }) : /* @__PURE__ */ jsx(ProtocolValue, { value });
  return /* @__PURE__ */ jsxs(SectionCard, { title: recovered ? "Bridged funds recovered" : "Destination operation completed", protocolId: "lifi", children: [
    /* @__PURE__ */ jsx("p", { className: "mb-3 text-xs text-muted-foreground", children: recovered ? "The destination operation was skipped or failed. LI.FI paid the bridged asset to the receiver instead of completing the requested swap." : "Check recipient transfers for the asset and amount paid. This completion event can report the bridged input asset alongside the output token's balance." }),
    /* @__PURE__ */ jsx(FactGrid, { facts: [
      { label: "Receiver", value: address(pickField(event.params, "receiver")) },
      { label: "Recovered asset", value: address(pickField(event.params, "receivingAssetId")), when: recovered },
      { label: "Recovered amount", value: /* @__PURE__ */ jsx(TokenQuantity, { value: pickField(event.params, "amount") }), when: recovered },
      { label: "LI.FI transfer ID", value: /* @__PURE__ */ jsx(ProtocolValue, { value: pickField(event.params, "transactionId") }) }
    ] }),
    /* @__PURE__ */ jsxs("details", { className: "mt-3", children: [
      /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground", children: "All event fields" }),
      /* @__PURE__ */ jsx(ProtocolParamList, { params: event.params, emptyLabel: "No decoded fields" })
    ] })
  ] });
}

// src/protocols/lifi/index.ts
var PID = LIFI.protocolId;
var call = (name) => ({
  protocolId: PID,
  call: name,
  component: LIFI_COMPONENTS.bridge,
  label: "Bridge via LI.FI",
  summaryFields: []
});
var LIFI_HINTS = {
  valueHints: [],
  eventHints: ["LiFiTransferCompleted", "LiFiTransferRecovered"].map((event) => ({
    protocolId: PID,
    event,
    component: "LiFiOutcome",
    summaryFields: []
  })),
  callHints: LIFI_BRIDGE_CALLS.map(call)
};
function registerLiFi(registry) {
  registry.registerCall(LIFI_COMPONENTS.bridge, LiFiBridge);
  registry.registerEvent("LiFiOutcome", LiFiOutcome);
  registry.registerHints(LIFI_HINTS);
  return registry;
}

export { LIFI_HINTS, LiFiBridge, registerLiFi };
//# sourceMappingURL=chunk-YU26HXI3.js.map
//# sourceMappingURL=chunk-YU26HXI3.js.map